import { useCallback, useEffect, useRef, useState } from 'react';

const BOUND_MIN = 0;
const BOUND_MAX = 5;
const STEP = 0.05;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function snap(v: number): number {
  const s = Math.round(v / STEP) * STEP;
  return clamp(s, BOUND_MIN, BOUND_MAX);
}

function valueFromPointer(clientX: number, track: DOMRect): number {
  const t = clamp((clientX - track.left) / track.width, 0, 1);
  return snap(BOUND_MIN + t * (BOUND_MAX - BOUND_MIN));
}

type Props = {
  valueMin: number;
  valueMax: number;
  onChange: (min: number, max: number) => void;
  className?: string;
};

/**
 * Intervalo 0–5 com dois polegares (estilo volume / “minhoca”): sobe a partir de 0 e desce a partir de 5.
 */
export function NotaIntervaloSlider({ valueMin, valueMax, onChange, className = '' }: Props) {
  const lo = Math.min(valueMin, valueMax);
  const hi = Math.max(valueMin, valueMax);
  const loRef = useRef(lo);
  const hiRef = useRef(hi);
  loRef.current = lo;
  hiRef.current = hi;

  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<'low' | 'high' | null>(null);

  const applyFromClientX = useCallback(
    (clientX: number, which: 'low' | 'high') => {
      const el = trackRef.current;
      if (!el) return;
      const v = valueFromPointer(clientX, el.getBoundingClientRect());
      const curLo = loRef.current;
      const curHi = hiRef.current;
      if (which === 'low') {
        onChange(Math.min(v, curHi), curHi);
      } else {
        onChange(curLo, Math.max(v, curLo));
      }
    },
    [onChange]
  );

  const moveNearestFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const v = valueFromPointer(clientX, el.getBoundingClientRect());
      const curLo = loRef.current;
      const curHi = hiRef.current;
      if (Math.abs(v - curLo) <= Math.abs(v - curHi)) {
        onChange(Math.min(v, curHi), curHi);
      } else {
        onChange(curLo, Math.max(v, curLo));
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => applyFromClientX(e.clientX, drag);
    const onUp = () => setDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, applyFromClientX]);

  const pct = (v: number) => ((v - BOUND_MIN) / (BOUND_MAX - BOUND_MIN)) * 100;
  const leftPct = pct(lo);
  const widthPct = Math.max(0, pct(hi) - pct(lo));

  return (
    <div className={`select-none ${className}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold text-gray-400 tabular-nums w-3 shrink-0 pt-0.5">0</span>
        <div
          ref={trackRef}
          className="relative flex-1 h-10 flex items-center touch-none min-w-[140px] px-2"
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest('[data-range-thumb]')) return;
            moveNearestFromClientX(e.clientX);
          }}
        >
          <div
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3.5 rounded-full bg-gradient-to-b from-gray-200 to-gray-300 shadow-inner border border-gray-200/90 pointer-events-none"
            aria-hidden
          />
          <div
            className="absolute h-3.5 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-orange-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] pointer-events-none border border-orange-600/25"
            style={{
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />

          <button
            type="button"
            data-range-thumb
            aria-label={`Nota mínima do filtro: ${lo.toFixed(2)}`}
            aria-valuemin={BOUND_MIN}
            aria-valuemax={BOUND_MAX}
            aria-valuenow={Number(lo.toFixed(2))}
            role="slider"
            className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border-2 border-orange-500 shadow-md cursor-grab active:cursor-grabbing hover:ring-4 hover:ring-orange-200/60 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
            style={{ left: `${pct(lo)}%` }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDrag('low');
            }}
          />
          <button
            type="button"
            data-range-thumb
            aria-label={`Nota máxima do filtro: ${hi.toFixed(2)}`}
            aria-valuemin={BOUND_MIN}
            aria-valuemax={BOUND_MAX}
            aria-valuenow={Number(hi.toFixed(2))}
            role="slider"
            className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border-2 border-orange-500 shadow-md cursor-grab active:cursor-grabbing hover:ring-4 hover:ring-orange-200/60 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
            style={{ left: `${pct(hi)}%` }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDrag('high');
            }}
          />
        </div>
        <span className="text-[10px] font-semibold text-gray-400 tabular-nums w-3 shrink-0 text-right pt-0.5">5</span>
      </div>
    </div>
  );
}
